import type { TellescopeMethodQueryFilterConfig, TellescopeUpdaterFn } from "../../hooks";
import type { ModelName, QueryMethodForModel } from "../../types";
import { getObjectEntries } from "../../utils";

export interface SplitMutationSideEffectUpdaterTypesReturn<Model extends ModelName> {
  withUpdaters: Partial<{
    [QMethod in QueryMethodForModel<Model>]: TellescopeMethodQueryFilterConfig<Model, QMethod> & {
      updater: TellescopeUpdaterFn<Model, QMethod>;
    };
  }>;
  withoutUpdaters: Partial<{
    [QMethod in QueryMethodForModel<Model>]: TellescopeMethodQueryFilterConfig<Model, QMethod>;
  }>;
}

/**
 * Splits out the configured side effect config from AUTOMATIC_MUTATION_SIDE_EFFECT_CONFIGS for a given model and mutation
 * method according to if they have updaters or not.
 *
 * @template Model - The Tellescope model name (e.g., "users", "appointments")
 * @template Method - The mutation method for the model (e.g., "create_user", "update_appointment")
 *
 * @param configs - Configurations for the model/method to be filtered
 */
export const splitMutationSideEffectUpdaterTypes = <Model extends ModelName>(
  config: Partial<{
    [QMethod in QueryMethodForModel<Model>]: TellescopeMethodQueryFilterConfig<Model, QMethod> & {
      updater?: TellescopeUpdaterFn<Model, QMethod>;
    };
  }> = {}
): SplitMutationSideEffectUpdaterTypesReturn<Model> => {
  const entries = getObjectEntries(config);
  return entries.reduce<SplitMutationSideEffectUpdaterTypesReturn<Model>>(
    (acc, [key, value]) => {
      if (!value) return acc;
      if (!value?.updater) {
        acc.withoutUpdaters[key] = value;
        return acc;
      }
      // @ts-expect-error TS doesn't know we've filtered out values without an updater
      acc.withUpdaters[key] = value;
      return acc;
    },
    { withUpdaters: {}, withoutUpdaters: {} }
  );
};
